/**
 * EmbeddedWalletWarningModal
 *
 * Displays a warning to users connecting via Phantom's embedded wallet (OAuth).
 * Embedded wallets have different behavior than extension wallets:
 * - No signing confirmation step (transaction proceeds automatically)
 * - May experience slower confirmation times
 * - Point rewards may take longer to reflect accurately
 */

import { AlertTriangle, Info, X } from 'lucide-react';

interface EmbeddedWalletWarningModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  chestCount: number;
  creditsRequired: string;
}

export function EmbeddedWalletWarningModal({
  isOpen,
  onConfirm,
  onCancel,
  chestCount,
  creditsRequired,
}: EmbeddedWalletWarningModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
      <div
        className="relative p-6 rounded-2xl max-w-md w-full"
        style={{
          backgroundColor: 'rgba(40, 40, 40, 0.98)',
          border: '2px solid #FB8B00',
          boxShadow: '0 0 30px rgba(251, 139, 0, 0.3)',
        }}
      >
        {/* Close Button */}
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Warning Icon */}
        <div className="flex justify-center mb-4">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgba(251, 139, 0, 0.2)' }}
          >
            <AlertTriangle className="w-8 h-8 text-[#FB8B00]" />
          </div>
        </div>

        {/* Title */}
        <h2
          className="text-2xl font-bold text-center mb-2"
          style={{
            fontFamily: 'Outfit, sans-serif',
            color: '#FB8B00',
          }}
        >
          Embedded Wallet Notice
        </h2>

        {/* Subtitle */}
        <p
          className="text-center mb-6"
          style={{
            fontFamily: 'Outfit, sans-serif',
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '14px',
          }}
        >
          You're using Phantom's embedded wallet (Google/Apple sign-in)
        </p>

        {/* Transaction Summary */}
        <div
          className="p-4 rounded-xl mb-4"
          style={{
            backgroundColor: 'rgba(80, 228, 249, 0.1)',
            border: '1px solid rgba(80, 228, 249, 0.3)',
          }}
        >
          <div className="flex justify-between items-center">
            <span style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.7)' }}>
              Opening:
            </span>
            <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 'bold', color: '#50E4F9' }}>
              {chestCount} Chest{chestCount > 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex justify-between items-center mt-2">
            <span style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.7)' }}>
              Cost:
            </span>
            <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 'bold', color: '#FEBB01' }}>
              {creditsRequired} Credits
            </span>
          </div>
        </div>

        {/* Warning Points */}
        <div className="space-y-3 mb-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-[#FB8B00] flex-shrink-0 mt-0.5" />
            <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '14px', color: 'rgba(255, 255, 255, 0.85)' }}>
              <strong>No signing popup:</strong> The transaction will proceed automatically after you confirm here.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-[#FB8B00] flex-shrink-0 mt-0.5" />
            <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '14px', color: 'rgba(255, 255, 255, 0.85)' }}>
              <strong>Confirmation delays:</strong> Embedded wallets may experience longer transaction confirmation times.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-[#FB8B00] flex-shrink-0 mt-0.5" />
            <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '14px', color: 'rgba(255, 255, 255, 0.85)' }}>
              <strong>Point updates:</strong> Points may take a moment to reflect accurately after the transaction.
            </p>
          </div>
        </div>

        {/* Tip */}
        <div
          className="p-3 rounded-lg mb-6"
          style={{
            backgroundColor: 'rgba(74, 222, 128, 0.1)',
            border: '1px solid rgba(74, 222, 128, 0.3)',
          }}
        >
          <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>
            💡 <strong style={{ color: '#4ADE80' }}>Tip:</strong> For a smoother experience with signing confirmations, consider using the Phantom browser extension instead.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 px-4 rounded-xl font-bold transition-all hover:opacity-80"
            style={{
              fontFamily: 'Outfit, sans-serif',
              backgroundColor: 'transparent',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              color: '#FFFFFF',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-3 px-4 rounded-xl font-bold transition-all hover:opacity-90"
            style={{
              fontFamily: 'Outfit, sans-serif',
              background: 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
              color: '#000000',
              boxShadow: '0 0 15px rgba(251, 139, 0, 0.4)',
            }}
          >
            Proceed Anyway
          </button>
        </div>
      </div>
    </div>
  );
}
